import React, { useState } from 'react';
import type { CmsPage } from '../types';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PageEditor } from '../components/Editor/PageEditor';
import { FileQuestion, PlusCircle, ArrowLeft } from 'lucide-react';

interface NotFoundPageProps {
  requestedSlug?: string;
  isAdmin: boolean;
  allPages: CmsPage[];
}

export const NotFoundPage: React.FC<NotFoundPageProps> = ({
  requestedSlug = '',
  isAdmin,
  allPages,
}) => {
  const [isCreating, setIsCreating] = useState(false);

  const cleanSlug = requestedSlug.replace(/^\/+|\/+$/g, '');
  const suggestedTitle = cleanSlug
    ? cleanSlug
        .split(/[-_]+/)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ')
    : 'New Page';

  const newDraftPage: CmsPage = {
    slug: cleanSlug || 'new-page',
    pageName: suggestedTitle,
    body: [
      {
        id: `block-${Date.now()}-1`,
        type: 'header',
        data: { text: suggestedTitle, level: 1 },
      },
      {
        id: `block-${Date.now()}-2`,
        type: 'paragraph',
        data: { text: 'Start writing your page content here...' },
      },
    ],
    metaDescription: '',
    navParentPageId: null,
    isMainMenuItem: false,
  };

  const breadcrumbs = [
    { label: 'Home', url: '/' },
    { label: 'Page Not Found' },
  ];

  return (
    <div className="not-found-container py-5">
      <div className="container">
        <Breadcrumbs items={breadcrumbs} />

        {isCreating && isAdmin ? (
          <div className="my-4">
            <h4 className="fw-bold mb-3">Create New Page: /{cleanSlug}</h4>
            <PageEditor
              initialPage={newDraftPage}
              allPages={allPages}
              onSaveSuccess={(saved) => {
                window.history.pushState(null, '', `/${saved.slug}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              onExit={() => setIsCreating(false)}
            />
          </div>
        ) : (
          <div className="card border rounded-4 shadow-sm p-5 text-center my-4 bg-body-tertiary">
            <FileQuestion size={48} className="text-secondary mx-auto mb-3" />
            <h1 className="display-6 fw-bold mb-2">Page Not Found</h1>
            <p className="lead text-secondary mb-4">
              The page <code className="fw-semibold">/{cleanSlug}</code> does not exist in the compendium.
            </p>

            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a
                href="/"
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState(null, '', '/');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
              >
                <ArrowLeft size={16} /> Return to Home
              </a>

              {isAdmin && cleanSlug && (
                <button
                  type="button"
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  onClick={() => setIsCreating(true)}
                >
                  <PlusCircle size={16} /> Create &quot;/{cleanSlug}&quot; with Site Editor
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
